import { useAddAnalysisToDashboard } from '@modules/analysis/hooks/useCreateAnalysis/useAddAnalysisToDashboard';
import { ChooseFeedPreviewTypeForm } from '@modules/common/components/ChooseFeedPreviewTypeForm';
import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import { Direction } from '@modules/common/types';
import { OpenDashboardLink } from '@modules/dashboard/components/OpenDashboardLink';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { EditableDashboardListItem } from '@modules/dashboard/types';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
} from '@mui/material';
import { SyntheticEvent, useState } from 'react';

interface AddFeedToDashboardDialogContentProps {
  analysisID: string;
  analysisTypeDisplayName: string;
  dashboard: EditableDashboardListItem;
}

export const AddFeedToDashboardDialogContent = ({
  analysisID,
  analysisTypeDisplayName,
  dashboard,
}: AddFeedToDashboardDialogContentProps) => {
  const { closeModal } = useMuiModalContext();
  const [shouldShowAsFeed, setShouldShowAsFeed] = useState(false);
  const [addAnalysisToDashboard, { loading: isLoading }] =
    useAddAnalysisToDashboard();
  const dashboardTypeDisplayName = useDashboardStore(
    ({ visual }) => visual.dashboardTypeDisplayName,
  );

  const { showSuccessNotification } = useNotificationContext();

  const onSubmit = async (eve: SyntheticEvent) => {
    eve.preventDefault();

    const analysesMetadata = dashboard?.metadataV2?.analysesMetadata
      ? [
          ...dashboard?.metadataV2?.analysesMetadata,
          { id: analysisID, shouldShowAsFeed },
        ]
      : [{ id: analysisID, shouldShowAsFeed }];

    await addAnalysisToDashboard({
      analysisID,
      dashboard,
      dashboardLayout: dashboard.metadataV2?.layout ?? { rows: [] },
      analysesMetadata,
      shouldRefetchAnalyses: true,
      cellInsertDirection: Direction.Bottom,
    });

    closeModal();

    showSuccessNotification({
      contentComponent: `This ${analysisTypeDisplayName} is added to "${dashboard.title}"`,
      props: {
        timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        action: <OpenDashboardLink dashboard={dashboard} />,
      },
    });
  };

  return (
    <>
      <ModalHeader title="Add Feed to Dashboard View" showCloseButton={false} />
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent dividers>
          <ChooseFeedPreviewTypeForm
            containrSx={{ sx: 4 }}
            shouldShowAsFeed={shouldShowAsFeed}
            setShouldShowAsFeed={(value) => {
              setShouldShowAsFeed(value);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => closeModal()}
              type="button"
              disabled={isLoading}
              endIcon={<EscHotKeyIcon isDisabled={isLoading} />}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isLoading}
              loadingPosition="end"
              endIcon={<ReturnHotKeyIcon isDisabled={isLoading} />}
            >
              {`Add To ${dashboardTypeDisplayName}`}
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Box>
    </>
  );
};
